import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import CourseCard from "@/components/CourseCard";
import { ArrowRight, Compass, BookOpen, GraduationCap, Award, Sparkles, Languages, MessageCircle, MapPin } from "lucide-react";

const STEPS = [
  { icon: BookOpen, label: "Choose a course" },
  { icon: Compass, label: "Preview for free" },
  { icon: GraduationCap, label: "Enroll online" },
  { icon: Sparkles, label: "Learn at your pace" },
  { icon: MessageCircle, label: "Complete quizzes" },
  { icon: Award, label: "Download certificate" },
];

const REASONS = [
  { title: "Affordable", desc: "Courses from just $19 USD. No surprise fees.", icon: "🍁" },
  { title: "Self-paced", desc: "Learn whenever, wherever — your schedule.", icon: "🕐" },
  { title: "Practical skills", desc: "Real Canadian context for life and work.", icon: "🛠️" },
  { title: "Certificate of completion", desc: "Earn a downloadable PDF certificate.", icon: "🎓" },
  { title: "English & French", desc: "Multi-language learning paths.", icon: "🗣️" },
  { title: "AI Tutor support", desc: "Friendly help inside every paid course.", icon: "✨" },
];

export default function Home() {
  useSeo({
    title: "Teacher4Me — Online + In-Person Learning for Canadian Families",
    description:
      "Personalised tutoring, career courses, free Canadian practice tests, and a NW Calgary Learning Centre. One Teacher4Me account for children, students, families, and adult learners.",
    canonical: "https://teacher4me.ca/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://teacher4me.ca/#org",
          "name": "Teacher4Me",
          "url": "https://teacher4me.ca",
          "logo": "https://teacher4me.ca/tmo-logo.png",
          "email": "teachmeonlineca@gmail.com",
          "telephone": "+1-825-365-8805",
          "areaServed": {"@type": "Country", "name": "Canada"},
          "sameAs": ["https://teacher4me.ca/calgary-nw"],
        },
        {
          "@type": "WebSite",
          "@id": "https://teacher4me.ca/#website",
          "url": "https://teacher4me.ca",
          "name": "Teacher4Me",
          "publisher": {"@id": "https://teacher4me.ca/#org"},
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://teacher4me.ca/courses?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "EducationalOrganization",
          "name": "Teacher4Me Learning Centre",
          "url": "https://teacher4me.ca/calgary-nw",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Unit 201A, 4716 16 Ave NW",
            "addressLocality": "Calgary",
            "addressRegion": "AB",
            "addressCountry": "CA",
          },
          "telephone": "+1-825-365-8805",
        },
      ],
    },
  });

  const [popular, setPopular] = useState([]);
  const [partnerCourses, setPartnerCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/api/courses").then(({ data }) => setPopular(data.slice(0, 6))).catch(() => {});
    api.get("/api/partner-courses?limit=6").then(({ data }) => setPartnerCourses(data)).catch(() => {});
    api.get("/api/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-paper border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="label-eyebrow flex items-center gap-2">
              <span className="w-6 h-px bg-[#D92D20]" /> Canadian online learning
            </div>
            <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter mt-3 leading-[1.02]">
              Learn Better. Grow Stronger. <span className="text-[#D92D20]">Succeed.</span>
            </h1>
            <p className="mt-6 text-lg text-[#4B5563] max-w-xl leading-relaxed">
              Personalized learning for students, online and in-person.
              Math • Science • Homework Help • Practice Tests • Online Learning.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/free-assessment" data-testid="hero-assessment" className="btn-primary flex items-center gap-2">
                BOOK FREE ASSESSMENT <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/courses" data-testid="hero-browse" className="btn-outline flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#064E3B]" /> Learn Online
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-sm text-[#4B5563]">
              <div><span className="font-display font-black text-2xl text-[#111827]">10+</span><div>Live courses</div></div>
              <div><span className="font-display font-black text-2xl text-[#111827]">10</span><div>Categories</div></div>
              <div><span className="font-display font-black text-2xl text-[#111827]">EN/FR</span><div>Languages</div></div>
            </div>
          </div>
          <div className="md:col-span-5 relative">
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1616769364512-1e50e8266907?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Student learning"
                className="w-full aspect-[4/5] object-cover rounded-2xl border border-[#E5E7EB] shadow-sm"
              />
              <div className="flex flex-col gap-4">
                <img
                  src="https://images.pexels.com/photos/7972958/pexels-photo-7972958.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Students studying"
                  className="w-full aspect-[4/3] object-cover rounded-2xl border border-[#E5E7EB] shadow-sm"
                />
                <div className="bg-[#064E3B] rounded-2xl p-5 text-white">
                  <Award className="w-6 h-6 text-[#FECACA]" />
                  <p className="mt-3 font-display font-bold text-lg leading-tight">Earn a Certificate of Completion</p>
                  <p className="text-xs mt-1 opacity-80">Downloadable PDF after final test.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NW CALGARY — physical centre band (Phase 1) */}
      <section className="bg-[#064E3B] text-white" data-testid="home-calgary-band">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#FDE68A]">
              Now enrolling · NW Calgary
            </div>
            <h2 className="font-display font-black text-2xl md:text-3xl mt-2 leading-tight">
              Math &amp; Science tutoring — in-person at our new Learning Centre.
            </h2>
            <p className="mt-3 text-white/80 text-sm leading-relaxed">
              Teacher4Me Learning Centre · Unit 201A, 4716 16 Ave NW, Calgary, Alberta ·
              Personalised learning, Alberta curriculum support, weekly parent progress reports.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 justify-start md:justify-end">
            <Link to="/free-assessment" data-testid="home-calgary-assessment"
              className="inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#B91C1C] text-white font-semibold px-5 py-3 rounded-md text-sm">
              BOOK FREE ASSESSMENT
            </Link>
            <Link to="/calgary-nw" data-testid="home-calgary-details"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-3 rounded-md text-sm">
              Learn more →
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-eyebrow">Browse by category</div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-1">Find what you need.</h2>
          </div>
          <Link to="/courses" data-testid="link-all-categories" className="hidden md:block text-sm font-semibold text-[#D92D20] hover:underline">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {cats.map((c) => (
            <Link
              key={c.id}
              to={`/courses?category=${c.slug}`}
              data-testid={`cat-${c.slug}`}
              className="card-lift bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#D92D20]"
            >
              <div className="w-10 h-10 rounded-lg bg-[#F3F4F6] flex items-center justify-center mb-3">
                <Languages className="w-5 h-5 text-[#D92D20]" />
              </div>
              <div className="font-display font-bold leading-tight">{c.name}</div>
              <div className="text-xs text-[#4B5563] mt-1 line-clamp-2">{c.description}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* POPULAR COURSES */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-eyebrow">Popular right now</div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-1">Popular courses.</h2>
          </div>
          <Link to="/courses" data-testid="link-all-courses" className="hidden md:block text-sm font-semibold text-[#D92D20] hover:underline">View all →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {popular.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </section>

      {/* FROM OUR TRAINING PARTNERS */}
      {partnerCourses.length > 0 && (
        <section className="bg-white border-y border-[#E5E7EB]" data-testid="home-partner-courses">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="label-eyebrow flex items-center gap-2 text-[#064E3B]">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B]" />
                  Dual-certified · Powered by Training Partners
                </div>
                <h2 className="font-display font-black text-3xl md:text-4xl mt-1">
                  From accredited <span className="text-[#064E3B]">training organizations</span>.
                </h2>
                <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
                  Every course below is delivered by an accredited partner and issues a co-branded
                  certificate with QR verification — trusted by employers and regulators.
                </p>
              </div>
              <Link to="/partners" data-testid="link-all-partners"
                className="text-sm font-semibold text-[#D92D20] hover:underline">
                Browse all partners →
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {partnerCourses.map((c) => <CourseCard key={c.id} course={c} />)}
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="label-eyebrow">How it works</div>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-1 mb-12">Six simple steps.</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.label} data-testid={`step-${i+1}`} className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FAFAFA] border border-[#E5E7EB] flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-[#D92D20]" />
                  </div>
                  <div className="label-eyebrow text-[10px]">Step {i+1}</div>
                  <div className="font-display font-bold mt-1 leading-tight">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARENT REPORTING — key differentiator */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="home-parent-reporting">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <span className="w-6 h-px bg-[#D92D20]" /> Parent visibility
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-3 tracking-tight leading-tight">
              Know what your child is learning.
            </h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">
              After every session Teacher4Me records the topic, work completed, teacher comments,
              strengths and areas needing improvement — all inside your child's Teacher4Me account.
            </p>
          </div>
          <ul className="grid grid-cols-2 gap-3 text-sm">
            {["Attendance", "Subject", "Topic covered", "Time studied",
              "Work completed", "Teacher comments", "Strengths",
              "Areas needing improvement", "Recommended next steps"].map((x) => (
              <li key={x} className="bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#064E3B]" /> {x}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MORE WAYS TO LEARN — services strip */}
      <section className="bg-white border-y border-[#E5E7EB]" data-testid="home-services-strip">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-1">
              <div className="label-eyebrow">Services</div>
              <h2 className="font-display font-black text-3xl mt-1 leading-tight">
                More ways to learn, grow &amp; succeed.
              </h2>
              <p className="text-sm text-[#4B5563] mt-3 leading-relaxed">
                From tutoring and after-school programs to STEM activities, career development
                and flexible learning support — Teacher4Me is growing into a complete learning
                and development destination.
              </p>
              <Link to="/services" data-testid="home-explore-services"
                className="btn-primary mt-5 inline-flex items-center gap-2">
                Explore All Services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <ul className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                "After-School", "Tutoring", "School Pickup",
                "STEM & AI", "Career Courses", "Camps",
              ].map((x) => (
                <li key={x}
                  className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl px-4 py-3 font-semibold flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#D92D20]" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="label-eyebrow flex items-center gap-2">
              <span className="w-6 h-px bg-[#D92D20]" /> Why TeachMeOnline.ca
            </div>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 leading-tight tracking-tight">
              Built for <span className="text-[#D92D20]">Canadian</span> learners.
            </h2>
            <p className="mt-5 text-[#4B5563]">
              From newcomers to seasoned professionals — practical, affordable courses you can finish.
            </p>
            <Link to="/about" data-testid="link-about" className="inline-block mt-6 btn-outline">About us</Link>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {REASONS.map((r) => (
              <div key={r.title} className="bg-white border border-[#E5E7EB] rounded-xl p-6">
                <div className="text-2xl">{r.icon}</div>
                <div className="font-display font-bold text-lg mt-2">{r.title}</div>
                <div className="text-sm text-[#4B5563] mt-1">{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS placeholder */}
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Priya R.", quote: "I started speaking English with my neighbours within two weeks. The lessons feel like a friendly coach.", role: "Newcomer, Toronto" },
              { name: "Mateus S.", quote: "Resume writing course got me three interview calls. Worth every dollar.", role: "Job seeker, Calgary" },
              { name: "Anh L.", quote: "Caregiver Fundamentals helped me prepare for my work — clear, respectful, easy to follow.", role: "Caregiver, Vancouver" },
            ].map((t) => (
              <div key={t.name} className="rounded-2xl p-6 border border-white/10 bg-white/5">
                <p className="text-lg leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5">
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="text-xs opacity-70">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24 text-center">
        <div className="label-eyebrow inline-flex items-center gap-2"><MapPin className="w-3 h-3 text-[#D92D20]" /> Ready when you are</div>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-3 max-w-3xl mx-auto leading-tight">
          Your next skill is a few clicks away.
        </h2>
        <div className="mt-8 flex items-center gap-3 justify-center flex-wrap">
          <button onClick={() => nav("/courses")} data-testid="cta-browse" className="btn-primary">Browse Courses</button>
          <button onClick={() => nav("/learning-path")} data-testid="cta-create" className="btn-secondary">Create My Learning Path</button>
        </div>
      </section>
    </div>
  );
}
